/* Публичная страница поездки.
   Вся геометрия висит на токенах, поэтому персонализация автора (кегль и
   плотность приезжают в ссылке — см. share/api.ts) работает и тут.
   Отдельно от планировщика здесь только ширина колонки: экран не рабочий стол
   с панелями, а документ, который читают. */

.page {
  --page-w: 1040px;
  --box-pad: var(--sp-5);
  /* Разделители между блоками — всегда волосок; --bw остаётся замкнутым
     обводкам (см. themes.css). */
  --rule: 1px solid var(--border);
  --title-fs: calc(var(--fs-xl) * 1.3);
  --answer-fs: calc(var(--fs-xl) * 1.7);

  min-height: 100dvh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  row-gap: var(--sp-7);
  padding-block: var(--sp-7) var(--sp-8);
  background: var(--bg);
  color: var(--text);
}

/* Единственная колонка содержимого: шапка, каждый блок и подвал берут левый и
   правый край отсюда, а не каждый свой. Разъехавшиеся края — то, из-за чего
   страница выглядела собранной наспех. */
.page > * {
  width: min(100%, var(--page-w));
  margin-inline: auto;
  padding-inline: var(--sp-6);
}

.main {
  display: grid;
  align-content: start;
  row-gap: var(--sp-7);
}

/* ─────────────────────────────── Шапка ──────────────────────────────────── */

/* По базовой линии, а не по центру: знак — это слово со строкой иконок под
   ним, и бейдж, выровненный по центру всего знака, повисает ниже слова. */
.header {
  display: flex;
  align-items: baseline;
  gap: var(--sp-4) var(--sp-5);
  padding-bottom: var(--sp-5);
  border-bottom: var(--rule);
}

.badge {
  flex: none;
  margin-inline-start: auto;
  padding: var(--sp-1) var(--sp-3);
  border: var(--bw) solid var(--border-strong);
  border-radius: var(--radius);
  background: var(--surface-2);
  color: var(--text-3);
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* ─────────────────────── Название и дата обновления ─────────────────────── */

.hero {
  display: grid;
  row-gap: var(--sp-3);
}

.tripTitle {
  margin: 0;
  font-size: var(--title-fs);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
  text-wrap: balance;
}

/* Разделитель рисуется псевдоэлементом, а не склейкой строк: строка ' · '
   посреди текста переносится на телефоне ровно по точке. */
.meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2) var(--sp-4);
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--text-2);
  font-size: var(--fs-sm);
}

.meta li + li::before {
  content: '·';
  margin-right: var(--sp-4);
  color: var(--text-3);
}

/* ───────────────────────── Ответ: сколько стоит ─────────────────────────── */

.answer {
  display: grid;
  row-gap: var(--sp-5);
  padding: var(--box-pad);
  border: var(--bw) solid var(--border);
  border-left: max(3px, var(--bw)) solid var(--variant-color, var(--border-strong));
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.answerLabel {
  margin: 0;
  color: var(--text-3);
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.answerPrice {
  margin: var(--sp-2) 0 0;
  font-size: var(--answer-fs);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.answerNoPrice {
  margin: var(--sp-2) 0 0;
  color: var(--text-2);
  font-size: var(--fs-lg);
}

.answerVariant {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin: var(--sp-2) 0 0;
  min-width: 0;
  color: var(--text-2);
  font-size: var(--fs-sm);
  overflow-wrap: anywhere;
}

.figures {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-5) var(--sp-6);
  margin: 0;
}

.figure {
  display: grid;
  row-gap: var(--sp-1);
}

.figureLabel {
  color: var(--text-3);
  font-size: var(--fs-xs);
}

.figureValue {
  margin: 0;
  font-size: var(--fs-lg);
  font-variant-numeric: tabular-nums;
}

@media (min-width: 720px) {
  .answer {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    column-gap: var(--sp-7);
    row-gap: 0;
  }
}

/* ─────────────────────────── Сравнение вариантов ────────────────────────── */

.compare,
.variants {
  display: grid;
  row-gap: var(--sp-4);
}

.sectionTitle {
  margin: 0;
  font-size: var(--fs-md);
  font-weight: 700;
  line-height: 1.3;
}

/* Таблица уезжает в собственную прокрутку, а не растягивает страницу: колонок
   столько, сколько вариантов, и на телефон они не помещаются никогда. */
.tableWrap {
  overflow: auto;
  max-width: 100%;
  border: var(--bw) solid var(--border);
  border-radius: var(--radius);
  background: var(--surface);
  box-shadow: var(--shadow-card);
  overscroll-behavior-inline: contain;
  -webkit-overflow-scrolling: touch;
}

/* border-collapse: separate, потому что схлопнутые границы рисует таблица, а не
   ячейка: у прилипшей шапки и прилипшей колонки метрик они просто исчезали
   из-под прокрутки. Здесь линии — это inset-тени самих ячеек. */
.table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--fs-md);
}

.metricCol {
  width: 22ch;
}

.table th,
.table td {
  padding: var(--sp-3) var(--box-pad);
  box-shadow: inset 0 -1px 0 var(--border);
}

.table tbody tr:last-child th,
.table tbody tr:last-child td {
  box-shadow: none;
}

/* Подписи метрик — влево и с переносом (в греческом «Χρόνος πόρτα σε πόρτα»),
   числа — вправо: только так десятичные точки встают в столбик, ради чего
   таблица и существует. */
.table th {
  text-align: left;
}

.table td,
.table thead th:not(:first-child) {
  text-align: right;
  white-space: nowrap;
}

.table thead th {
  position: sticky;
  top: 0;
  background: var(--surface-2);
  color: var(--text-2);
  font-weight: 600;
}

.table tbody th {
  background: var(--surface);
  color: var(--text-2);
  font-weight: 500;
  white-space: normal;
  overflow-wrap: break-word;
}

.table tbody td {
  color: var(--text-2);
}

.variantHeader {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--sp-2);
  max-width: 100%;
}

.variantName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Точка меряется кеглем: при 150% рядом с крупным текстом фиксированные 8px
   превращаются в соринку. */
.dot {
  display: inline-block;
  width: 0.62em;
  height: 0.62em;
  border-radius: 50%;
  flex: 0 0 auto;
}

/* Лучшее значение — оттенок --ok. Раньше это был --surface-2, то есть ровно
   заливка шапки: выигравшая ячейка читалась как ещё один заголовок.
   --warn/--error по контракту заняты валидацией и сюда не годятся. */
.table td.best {
  background: color-mix(in srgb, var(--ok) 10%, var(--surface));
  color: var(--text);
  font-weight: 700;
}

/* Галочка стоит слева от числа, а само число прижато к правому краю — цифры
   от неё не съезжают. Свой цвет обязателен: в строке предупреждений на ячейке
   висит инлайновый --error, и без этого «лучшее» окрашивалось в красный. */
.checkmark {
  float: left;
  color: var(--ok);
  font-weight: 700;
}

/* ──────────────────────────── Карточки вариантов ────────────────────────── */

.cards {
  display: grid;
  grid-template-columns: repeat(var(--cards, 1), minmax(0, 1fr));
  gap: var(--sp-5);
  align-items: start;
}

@media (min-width: 721px) {
  .cards[data-count='2'] {
    --cards: 2;
  }

  .cards[data-count='3'] {
    --cards: 3;
  }

  /* Четыре — это 2×2, а не 3+1: одинокая четвёртая карточка во всю ширину
     читается как отдельный блок, а не как член ряда. */
  .cards[data-count='4'] {
    --cards: 2;
  }
}

@media (min-width: 721px) and (max-width: 1023px) {
  .cards[data-count='3'] {
    --cards: 2;
  }
}

/* Цвет варианта обводит карточку целиком — так же, как колонку варианта в
   планировщике: читатель, который потом заведёт свою поездку, встретит ту же
   примету «это тот самый вариант». */
.card {
  display: grid;
  row-gap: var(--sp-4);
  padding: var(--box-pad);
  border: max(2px, var(--bw)) solid var(--variant-color, var(--border-strong));
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.cardHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-3);
}

/* min-width: 0 — иначе длинное название отказывается сжиматься и выталкивает
   бейдж за край карточки. */
.cardName {
  min-width: 0;
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.cardBadge {
  flex: none;
  padding: 0 var(--sp-2);
  border: var(--bw) solid color-mix(in srgb, var(--ok) 40%, var(--border));
  border-radius: var(--radius);
  background: color-mix(in srgb, var(--ok) 12%, var(--surface));
  color: var(--ok);
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.cardPrice {
  margin: 0;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.cardPriceNote {
  margin: var(--sp-1) 0 0;
  color: var(--text-3);
  font-size: var(--fs-xs);
}

.cardFigures {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2) var(--sp-5);
  margin: 0;
  color: var(--text-2);
  font-size: var(--fs-sm);
}

.cardFigures:empty {
  display: none;
}

.segments {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  border-top: var(--rule);
}

/* Три дорожки на всю карточку: иконка, текст, цена. Сетка общая для всех строк,
   поэтому цены стоят в одну колонку у правого края. */
.segment {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  align-items: start;
  column-gap: var(--sp-4);
  padding-block: var(--sp-3);
  border-bottom: var(--rule);
}

.segment:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

/* Тип блока живёт в иконке, а цвет варианта — в обводке карточки: два акцента
   на одной строке спорили бы друг с другом. */
.segment[data-accent='flight'] {
  --accent: var(--flight);
  --accent-bg: var(--flight-bg);
}

.segment[data-accent='transfer'] {
  --accent: var(--transfer);
  --accent-bg: var(--transfer-bg);
}

.segment[data-accent='lodging'] {
  --accent: var(--lodging);
  --accent-bg: var(--lodging-bg);
}

.segment[data-accent='cost'] {
  --accent: var(--cost);
  --accent-bg: var(--cost-bg);
}

.segIcon {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border-radius: var(--radius);
  background: var(--accent-bg);
  color: var(--accent);
}

.segText {
  display: grid;
  min-width: 0;
  row-gap: 1px;
}

.segTitle {
  color: var(--text);
  font-size: var(--fs-sm);
  overflow-wrap: anywhere;
}

.segSubtitle {
  color: var(--text-3);
  font-size: var(--fs-xs);
  overflow-wrap: anywhere;
}

.segPrice {
  justify-self: end;
  text-align: right;
  white-space: nowrap;
  color: var(--text-2);
  font-size: var(--fs-sm);
  font-variant-numeric: tabular-nums;
}

.segPriceZero {
  color: var(--text-3);
}

/* ───────────────────────── Состояния и подвал ───────────────────────────── */

.skeleton {
  display: grid;
  row-gap: var(--sp-6);
}

.skelBlock {
  border-radius: var(--radius-lg);
  background: var(--surface-2);
}

.skelHero {
  height: calc(var(--answer-fs) * 3);
}

.skelTable {
  height: 240px;
}

/* Карточка состояния — единственное, что есть на странице, поэтому она стоит
   по центру свободного места, а не жмётся к левому верхнему углу. */
.stateWrap {
  display: grid;
  place-items: center;
  padding-block: var(--sp-8);
}

.stateCard {
  display: grid;
  justify-items: start;
  row-gap: var(--sp-5);
  width: 100%;
  max-width: 42ch;
  padding: var(--sp-7);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
}

.stateTitle {
  margin: 0;
  color: var(--text-2);
  font-size: var(--fs-lg);
}

.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-5);
  padding-top: var(--sp-6);
  border-top: var(--rule);
}

.footerNote {
  margin: 0;
  color: var(--text-2);
  font-size: var(--fs-sm);
}

/* ── Мобильный вид (порог продублирован в ui/shell/useIsMobile.ts) ────────
   По ссылке чаще всего приходят с телефона: столбец метрик остаётся на виду
   при прокрутке вбок, иначе, доехав до третьего варианта, уже не понять, что
   за строка перед тобой. */
@media (max-width: 720px) {
  .page > * {
    padding-inline: var(--sp-5);
  }

  .tableWrap {
    max-height: 60vh;
    overscroll-behavior: contain;
  }

  /* Прокрутка вбок важнее устойчивой ширины колонок: главное, чтобы ничего не
     было сплющено. */
  .table {
    table-layout: auto;
    width: max-content;
    min-width: 100%;
  }

  .metricCol {
    width: 14ch;
  }

  .table thead th {
    z-index: 2;
  }

  .table tbody th,
  .table thead th:first-child {
    position: sticky;
    left: 0;
    max-width: 40vw;
    /* Своя правая грань: иначе данные заезжают под прилипшую колонку без
       всякой видимой границы. */
    box-shadow: inset -1px 0 0 var(--border), inset 0 -1px 0 var(--border);
  }

  .table tbody th {
    z-index: 1;
  }

  .table thead th:first-child {
    z-index: 3;
  }

  .footer {
    flex-direction: column;
    align-items: stretch;
  }

  .footerCta {
    justify-content: center;
  }
}
